/**
 * FloatingToolsContext — manages the floating Notepad / Sticky Notes /
 * Calculator mini-windows. Triggered from the sidebar (no launcher pill).
 *
 * Windows registry shape: { [toolId]: { x, y, w, h, minimized, z } }
 * Persisted to localStorage so layout survives page navigation + reload.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "hbf_floating_tools_v1";
const LEGACY_STORAGE_KEYS = ["vocalis_floating_tools_v1"];

const DEFAULTS = {
  notepad:    { x: 140, y: 90,  w: 720, h: 560 },
  notes:      { x: 240, y: 130, w: 520, h: 560 },
  calculator: { x: 340, y: 170, w: 320, h: 480 },
};

const Ctx = createContext(null);

export function FloatingToolsProvider({ children }) {
  const [windows, setWindows] = useState(() => {
    try {
      let raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        for (const legacy of LEGACY_STORAGE_KEYS) {
          const v = localStorage.getItem(legacy);
          if (v) { raw = v; localStorage.setItem(STORAGE_KEY, v); localStorage.removeItem(legacy); break; }
        }
      }
      return JSON.parse(raw)?.windows || {};
    }
    catch { return {}; }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ windows }));
    } catch (e) {
      // Storage quota exceeded or private-browsing mode — windows layout will
      // simply not persist across reloads. Non-fatal; fine to swallow.
      if (process.env.NODE_ENV !== "production") console.debug("floating-tools: persist failed", e);
    }
  }, [windows]);

  const openTool = useCallback((id) => {
    setWindows((w) => {
      const base = DEFAULTS[id] || { x: 200, y: 120, w: 480, h: 520 };
      if (w[id]) {
        // already open — un-minimize and bring to front
        return { ...w, [id]: { ...w[id], minimized: false, z: Date.now() } };
      }
      return { ...w, [id]: { ...base, minimized: false, z: Date.now() } };
    });
  }, []);

  const closeTool = useCallback((id) => {
    setWindows((w) => { const { [id]: _, ...rest } = w; return rest; });
  }, []);

  const minimizeTool = useCallback((id) => {
    setWindows((w) => ({ ...w, [id]: { ...w[id], minimized: !w[id].minimized, z: Date.now() } }));
  }, []);

  const updateWindow = useCallback((id, patch) => {
    setWindows((w) => ({ ...w, [id]: { ...w[id], ...patch } }));
  }, []);

  const bringToFront = useCallback((id) => {
    setWindows((w) => ({ ...w, [id]: { ...w[id], z: Date.now() } }));
  }, []);

  const value = useMemo(() => ({
    windows, openTool, closeTool, minimizeTool, updateWindow, bringToFront,
  }), [windows, openTool, closeTool, minimizeTool, updateWindow, bringToFront]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFloatingTools() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useFloatingTools must be used inside FloatingToolsProvider");
  return ctx;
}
